import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";

// In-memory cache for translated CMS content
const translationCache = new Map<string, string>();

export function useTranslatedContent(text: string | null | undefined, cacheKey?: string) {
  const { language } = useLanguage();
  const [translated, setTranslated] = useState(text || "");
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!text) {
      setTranslated("");
      return;
    }

    if (language === "en") {
      setTranslated(text);
      return;
    }

    const key = cacheKey || text.slice(0, 100);
    const cached = translationCache.get(key);
    if (cached) {
      setTranslated(cached);
      return;
    }

    let cancelled = false;
    setIsTranslating(true);

    supabase.functions
      .invoke("translate-content", {
        body: { text, targetLanguage: "Arabic" },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("Translation error:", error);
          setTranslated(text);
        } else {
          const result = data?.translatedText || text;
          translationCache.set(key, result);
          setTranslated(result);
        }
      })
      .finally(() => {
        if (!cancelled) setIsTranslating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [text, language, cacheKey]);

  return { translated, isTranslating };
}

export function useStaticTranslation() {
  const { t, language, isRTL } = useLanguage();
  return { t, language, isRTL };
}
